import { motion } from "framer-motion";
import { Heart, Flame, Crown, Users, Star } from "lucide-react";

const WORSHIP_IMG = "https://images.unsplash.com/photo-1648187311768-fe80f1ac43a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxibGFjayUyMHBlb3BsZSUyMGNodXJjaCUyMHdvcnNoaXAlMjBwcmF5ZXIlMjBoYW5kcyUyMHJhaXNlZHxlbnwwfHx8fDE3NzYwODg2NTN8MA&ixlib=rb-4.1.0&q=85";

const pillars = [
  {
    icon: Flame,
    title: "Power & Purpose",
    text: "To establish God's dominion on earth by preaching the gospel, empowering lives through the Holy Spirit, and advancing His kingdom.",
    color: "text-crimson",
    bg: "bg-crimson/5",
    border: "border-crimson/10",
  },
  {
    icon: Heart,
    title: "Revival & Impact",
    text: "Igniting revival, building disciples, and demonstrating the power of the Holy Spirit to bring transformation to our community and beyond.",
    color: "text-cgreen",
    bg: "bg-cgreen/5",
    border: "border-cgreen/10",
  },
  {
    icon: Crown,
    title: "Worship & Presence",
    text: "To cultivate a dwelling place of God's presence where people are saved, healed, empowered, and released into their divine purpose.",
    color: "text-navy",
    bg: "bg-navy/5",
    border: "border-navy/10",
  },
  {
    icon: Users,
    title: "Leadership & Authority",
    text: "Raising leaders who carry God's presence, walk in spiritual authority, and influence every sphere of life for Christ.",
    color: "text-crimson",
    bg: "bg-crimson/5",
    border: "border-crimson/10",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-24 px-6 bg-white relative overflow-hidden">
      {/* Decorative blob */}
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-navy/3 blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-cgreen/3 blur-3xl" />

      <div className="max-w-7xl mx-auto relative">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-navy/5 text-navy text-xs font-body font-bold uppercase tracking-[0.2em] mb-6">
            <Star className="w-3.5 h-3.5" />
            About Us
          </span>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-navy mb-4">
            Our Mission
          </h2>
          <p className="font-body text-slate-600 text-base max-w-2xl mx-auto leading-relaxed">
            At Dominion Parish, we exist to raise Spirit-filled believers who walk in authority,
            manifest God's power, and transform their world through Jesus Christ.
          </p>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {/* Image card - spans 1 col on lg */}
          <motion.div
            className="relative rounded-3xl overflow-hidden h-72 md:h-auto md:row-span-2 shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img
              src={WORSHIP_IMG}
              alt="Worship community"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-white font-heading font-bold text-xl">
                Empowered to reign.
              </p>
              <p className="text-white/70 font-body text-sm mt-1">
                Called to transform.
              </p>
            </div>
          </motion.div>

          {/* Pillar cards */}
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className={`p-8 rounded-3xl border ${pillar.border} ${pillar.bg} shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
              data-testid={`pillar-card-${i}`}
            >
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${pillar.bg} mb-5`}>
                <pillar.icon className={`w-6 h-6 ${pillar.color}`} />
              </div>
              <h3 className="font-heading font-semibold text-xl text-navy mb-3">
                {pillar.title}
              </h3>
              <p className="font-body text-slate-600 text-sm leading-relaxed">
                {pillar.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Tagline */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <p className="font-heading font-bold text-xl sm:text-2xl text-navy/80 italic">
            "Taking dominion through Christ."
          </p>
          <p className="font-body text-slate-500 text-sm mt-2">
            Most importantly &mdash; to make heaven.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
